import { useCallback, useEffect, useState } from "react"
import { Mail, Pencil, LogOut } from "lucide-react"
import { useAuth, getStoredToken } from "../../../hooks/useAuth"
import { useI18n } from "../../../i18n"
import type { TranslationKey } from "../../../i18n/types"
import type { PanelProps } from "../types"

// ─── Connections inventory types (C1, HF-6 Slice 1) ───────────────────────────
// Mirrors src/gateway/connections-status.ts's ConnectorRow/ConnectionsResponse
// exactly — server-authoritative, read-only. `label`/`reason` are i18n KEYS
// (see the server module's doc comment), cast to TranslationKey the same way
// SchedulesPanel.tsx already casts server-sourced key strings.

type ConnectorKind = "oauth" | "mcp" | "local-service" | "api-key"
type ConnectorStatus = "connected" | "needs_auth" | "unavailable" | "not_wired"

interface ConnectorRow {
  id: string
  label: string
  kind: ConnectorKind
  status: ConnectorStatus
  detail?: string
  reason?: string
  canConnect: boolean
}

const STATUS_LABEL_KEY: Record<ConnectorStatus, TranslationKey> = {
  connected: "settings.connections.status.connected",
  needs_auth: "settings.connections.status.needsAuth",
  unavailable: "settings.connections.status.unavailable",
  not_wired: "settings.connections.status.notWired",
}

const STATUS_COLOR: Record<ConnectorStatus, string> = {
  connected: "var(--color-success, #22863a)",
  needs_auth: "var(--color-warning, #d97706)",
  unavailable: "var(--color-error, #c0392b)",
  not_wired: "var(--text-muted)",
}

// The MCP row's label is a generic i18n key ("MCP server") — the actual
// server name is operator-configured data, not code-authored copy (same
// class of thing as an account email), and travels in `id` as `mcp:<name>`.
function mcpServerNameFromId(id: string): string | null {
  return id.startsWith("mcp:") ? id.slice(4) : null
}

// ─── Google Accounts types ────────────────────────────────────────────────────
// Relocated from SettingsPanel.tsx as-is — no new capability. C1 (HF-6) grows
// this panel (linked accounts beyond Google, more than the three fixed slots).

interface GoogleAccount {
  account_slot: string
  account_email: string
  display_name: string | null
  picture_url: string | null
  status: string
  last_error: string | null
  scopes: string[]
  connected_at: number
}

// Scopes added in Task #3 (Docs/Sheets sprint) plus Contacts (Funkatorium
// Office blueprint, 2026-10-01). Connected slots that pre-date either change
// will be missing these scopes and need a passive re-consent prompt. Renamed
// from DOCS_SHEETS_SCOPES when Contacts was added — mirrors
// src/gateway/connections-status.ts's DOCS_SHEETS_CONTACTS_SCOPES, same
// rename, same reason. Grep the other side before changing this list.
const DOCS_SHEETS_CONTACTS_SCOPES = [
  'https://www.googleapis.com/auth/documents',
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/contacts',
] as const

function isMissingDocsScopes(account: GoogleAccount | undefined): boolean {
  if (!account) return false
  return DOCS_SHEETS_CONTACTS_SCOPES.some((s) => !account.scopes.includes(s))
}

type GoogleSlot = 'personal' | 'funkatorium' | 'legal' | 'devops'

// Slot label/purpose copy lives in i18n (settings.connections.google.slot.<slot>.label|purpose).
const SLOT_LABEL_KEYS: Record<GoogleSlot, TranslationKey> = {
  personal: "settings.connections.google.slot.personal.label",
  funkatorium: "settings.connections.google.slot.funkatorium.label",
  legal: "settings.connections.google.slot.legal.label",
  devops: "settings.connections.google.slot.devops.label",
}

const SLOT_PURPOSE_KEYS: Record<GoogleSlot, TranslationKey> = {
  personal: "settings.connections.google.slot.personal.purpose",
  funkatorium: "settings.connections.google.slot.funkatorium.purpose",
  legal: "settings.connections.google.slot.legal.purpose",
  devops: "settings.connections.google.slot.devops.purpose",
}

// The Google tool server's connector id, per claude-mcp-config.ts googleServers():
// canonical `mcp:google` or `<identity>-google` (case-insensitive). The server
// also matches by proxy-script args, which the client cannot see — such a
// server just gets no summary line.
function isGoogleServerId(id: string): boolean {
  const lower = id.toLowerCase()
  return lower === "mcp:google" || lower.endsWith("-google")
}

// ─── OAuth redirect-back marker ────────────────────────────────────────────────
// handleOAuthCallback (gateway oauth-handlers.ts) redirects here with
// `?google=connected&slot=<slot>` or `?google=error&code=<fixed-code>` — a
// fixed short code, never the raw provider error text. One-shot: read once,
// then stripped from the URL so a reload/back-nav doesn't replay it.
const GOOGLE_OAUTH_ERROR_MESSAGES: Record<string, string> = {
  denied: "Google sign-in was cancelled.",
  bad_request: "Google's response was missing required parameters. Try again.",
  state_mismatch: "That connection attempt expired or was invalid. Try again.",
  exchange_failed: "Could not complete authorization with Google. Try again.",
  no_refresh_token: "Google did not grant persistent access. Try revoking access in your Google account and reconnecting.",
  userinfo_failed: "Google let you in, but we couldn't load your account info. Try again.",
  server_error: "Something went wrong on our end. Try again.",
}

// Google and Discord SVG icons — inline to avoid extra deps
function GoogleIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}

// PanelProps.activeIdentity threads the current companion — connector status
// is identity-scoped (MCP servers, X account, Claude Code binary), unlike
// the Google Accounts section below which has no companion notion at all.
export function ConnectionsPanel({ activeIdentity }: PanelProps) {
  const { user, logout, refetch } = useAuth()
  const { t } = useI18n()

  // Display name inline edit state
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState("")
  const [nameSaved, setNameSaved] = useState(false)
  const [nameSaving, setNameSaving] = useState(false)

  // Google Accounts state
  const [googleAccounts, setGoogleAccounts] = useState<GoogleAccount[]>([])
  const [googleConnecting, setGoogleConnecting] = useState(false)
  const [googleError, setGoogleError] = useState<string | null>(null)

  const [googleDisconnecting, setGoogleDisconnecting] = useState<string | null>(null)

  // Connections inventory state (C1, HF-6 Slice 1)
  const [connectors, setConnectors] = useState<ConnectorRow[] | null>(null)
  const [connectorsError, setConnectorsError] = useState<string | null>(null)

  // Sync name input as user data loads. The panel mounts fresh each time the
  // surface opens on this section (SettingsSurface unmounts everything on
  // close), so there is no separate "reset on open" case to handle here —
  // useState's initial values already cover it.
  useEffect(() => {
    setNameValue(user?.display_name ?? "")
  }, [user?.display_name])

  // Fetch Google account status on mount.
  const fetchGoogleAccounts = useCallback(async () => {
    try {
      const authToken = getStoredToken()
      if (!authToken) return
      const res = await fetch("/api/google/accounts", {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) return
      const data = await res.json() as { accounts: GoogleAccount[] }
      setGoogleAccounts(data.accounts)
    } catch {
      // Non-fatal — OAuth section simply shows no accounts
    }
  }, [])

  useEffect(() => {
    void fetchGoogleAccounts()
  }, [fetchGoogleAccounts])

  // Read the one-shot OAuth redirect-back marker (see the module doc comment
  // above GOOGLE_OAUTH_ERROR_MESSAGES) and clear it immediately so a reload
  // or Back navigation never replays a stale success/error banner.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const result = params.get("google")
    if (!result) return

    if (result === "connected") {
      setGoogleError(null)
      void fetchGoogleAccounts()
    } else if (result === "error") {
      const code = params.get("code") ?? ""
      setGoogleError(GOOGLE_OAUTH_ERROR_MESSAGES[code] ?? "Connection failed. Try again.")
    }

    params.delete("google")
    params.delete("slot")
    params.delete("code")
    const qs = params.toString()
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash)
  }, [fetchGoogleAccounts])

  // Fetch the connector inventory on mount, and again if the active
  // companion changes (status is identity-scoped — see PanelProps.activeIdentity).
  const fetchConnections = useCallback(async () => {
    try {
      const authToken = getStoredToken()
      if (!authToken) return
      const query = activeIdentity ? `?companion=${encodeURIComponent(activeIdentity)}` : ""
      const res = await fetch(`/api/connections${query}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) {
        setConnectorsError(t("settings.connections.error"))
        return
      }
      const data = await res.json() as { connectors: ConnectorRow[] }
      setConnectors(data.connectors)
      setConnectorsError(null)
    } catch {
      setConnectorsError(t("settings.connections.error"))
    }
  }, [activeIdentity, t])

  useEffect(() => {
    void fetchConnections()
  }, [fetchConnections])

  async function handleConnectGoogle(slot: GoogleSlot) {
    setGoogleConnecting(true)
    setGoogleError(null)
    try {
      const authToken = getStoredToken()
      if (!authToken) {
        setGoogleError("Not authenticated")
        setGoogleConnecting(false)
        return
      }
      const res = await fetch(`/api/auth/google/start?account_slot=${slot}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) {
        const err = await res.json() as { message?: string }
        setGoogleError(err.message ?? `Server error: ${res.status}`)
        setGoogleConnecting(false)
        return
      }
      const { authorize_url } = await res.json() as { authorize_url: string }

      // Same-window top-level navigation, not window.open(). A popup opened
      // after this await has no user-activation left, so Safari (macOS and
      // iOS) and popup blockers generally block it silently — the UI would
      // hang on "Connecting..." forever with nothing visible to the user. An
      // installed iOS home-screen PWA (standalone display mode) also has no
      // window.opener/postMessage channel back to this tab even when a popup
      // does open. A top-level location change after an await is NOT popup-
      // blocked, and works the same in a browser tab or a standalone PWA.
      // The OAuth callback (oauth-handlers.ts handleOAuthCallback) redirects
      // back to this page when it's done — the effect above reads the result.
      window.location.assign(authorize_url)
      // No setGoogleConnecting(false) here: the page is navigating away, so
      // this component is about to unmount. If the navigation gets cancelled
      // the "Connecting..." state will look stuck until the next interaction
      // — there's no event to detect a cancelled top-level navigation from.
    } catch (err) {
      setGoogleError(err instanceof Error ? err.message : "Connection failed")
      setGoogleConnecting(false)
    }
  }

  async function handleDisconnectGoogle(slot: GoogleSlot) {
    const label = t(SLOT_LABEL_KEYS[slot])
    if (!window.confirm(t("settings.connections.google.remove.confirm", { label }))) return
    setGoogleDisconnecting(slot)
    setGoogleError(null)
    try {
      const authToken = getStoredToken()
      if (!authToken) {
        setGoogleError("Not authenticated")
        return
      }
      const res = await fetch(`/api/google/accounts/${slot}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) {
        setGoogleError(`Could not disconnect ${label} (server error ${res.status})`)
        return
      }
      await fetchGoogleAccounts()
    } catch (err) {
      setGoogleError(err instanceof Error ? err.message : "Disconnect failed")
    } finally {
      setGoogleDisconnecting(null)
    }
  }

  // Why "Connected" on the tool server can sit beside red Google slots: the
  // server is up, but individual Google accounts may need re-consent.
  const googleBroken = googleAccounts.filter((a) => a.status !== "connected").length
  const googleConnected = googleAccounts.length - googleBroken

  function startEditName() {
    setNameValue(user?.display_name ?? "")
    setEditingName(true)
    setNameSaved(false)
  }

  async function saveName() {
    if (nameSaving) return
    const trimmed = nameValue.trim()
    // No change — just exit edit mode
    if (trimmed === (user?.display_name ?? "")) {
      setEditingName(false)
      return
    }
    setNameSaving(true)
    try {
      const res = await fetch("/api/auth/display-name", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_name: trimmed }),
      })
      if (res.ok) {
        setNameSaved(true)
        setEditingName(false)
        await refetch()
        setTimeout(() => setNameSaved(false), 2000)
      }
    } finally {
      setNameSaving(false)
    }
  }

  function handleNameKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") saveName()
    if (e.key === "Escape") setEditingName(false)
  }

  function handleLogout() {
    if (window.confirm(t("settings.account.logoutConfirm"))) {
      logout()
    }
  }

  return (
    <>
      {/* ACCOUNT section */}
      <div className="settings-section">
        <div className="settings-section-label">{t("settings.account")}</div>

        {/* Email */}
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.account.email")}</div>
            <div className="settings-row-hint">{t("settings.account.emailHint")}</div>
          </div>
          <div className="settings-row-value" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Mail size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
            <span>{user?.email ?? ""}</span>
          </div>
        </div>

        {/* Display Name */}
        <div className="settings-row">
          <div className="settings-row-label">{t("settings.account.displayName")}</div>
          <div>
            {editingName ? (
              <input
                className="settings-inline-input"
                type="text"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onBlur={saveName}
                onKeyDown={handleNameKeyDown}
                maxLength={64}
                autoFocus
                disabled={nameSaving}
              />
            ) : (
              <button
                type="button"
                className="settings-inline-edit btn-ghost"
                onClick={startEditName}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              >
                <span className="settings-row-value">
                  {nameSaved
                    ? t("settings.account.nameSaved")
                    : (user?.display_name ?? "—")}
                </span>
                <Pencil size={13} style={{ opacity: 0.5, marginLeft: 6 }} />
              </button>
            )}
          </div>
        </div>

        {/* Logout */}
        <div className="settings-row" style={{ marginTop: "var(--space-2)" }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleLogout}
            style={{ width: "100%", justifyContent: "center", color: "var(--text-muted)" }}
          >
            <LogOut size={15} style={{ marginRight: 6 }} />
            {t("settings.account.logout")}
          </button>
        </div>
      </div>

      {/* GOOGLE ACCOUNTS section — first, because this is what you manage */}
      <div className="settings-section">
        <div className="settings-section-label">Google Accounts</div>

        {/* Passive re-consent banner: shown when any connected slot is missing
            the Docs/Sheets/Drive scopes added in the Task #3 sprint. Surfaces
            per-slot — never force-prompts all slots simultaneously. */}
        {(["personal", "funkatorium", "legal", "devops"] as const).some((slot) =>
          isMissingDocsScopes(googleAccounts.find(a => a.account_slot === slot))
        ) && (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: "var(--color-warning-bg, rgba(217, 119, 6, 0.08))",
              border: "1px solid var(--color-warning-border, rgba(217, 119, 6, 0.3))",
              borderRadius: "var(--radius-sm, 6px)",
              padding: "var(--space-2) var(--space-3)",
              marginBottom: "var(--space-2)",
              fontSize: "var(--text-xs)",
              color: "var(--color-warning, #d97706)",
            }}
          >
            <strong>Docs &amp; Sheets tools need updated permissions.</strong>
            {" "}Reconnect the accounts below to authorize Google Docs, Sheets, and Drive access.
          </div>
        )}

        {/* Render a slot row with status-aware UI. Four states:
              - connected + full scopes:    green hint, no button
              - connected + missing scopes: amber hint, Reconnect for Docs/Sheets button
              - needs_reauth:               amber hint + last_error, Reconnect button
              - absent:                     grey hint, Connect button */}
        {(["personal", "funkatorium", "legal", "devops"] as const).map((slot) => {
          const account = googleAccounts.find(a => a.account_slot === slot)
          const label = t(SLOT_LABEL_KEYS[slot])
          const isConnected = account?.status === "connected"
          const needsReauth = account != null && !isConnected
          const missingScopes = isConnected && isMissingDocsScopes(account)
          // Show reconnect button when: not connected, needs_reauth, or missing Docs/Sheets scopes
          const showReconnect = !isConnected || missingScopes

          return (
            <div key={slot} className="settings-row">
              <div>
                <div className="settings-row-label">{label}</div>
                <div className="settings-row-hint">{t(SLOT_PURPOSE_KEYS[slot])}</div>
                {/* D.3: aria-live so screen readers hear the post-OAuth status update */}
                <div aria-live="polite" aria-atomic="true">
                  {isConnected && !missingScopes && (
                    <div className="settings-row-hint" style={{ color: "var(--color-success, #22863a)" }}>
                      {t("settings.google.status.connectedAs", { email: account.account_email })}
                    </div>
                  )}
                  {isConnected && missingScopes && (
                    <div className="settings-row-hint" style={{ color: "var(--color-warning, #d97706)" }}>
                      {t("settings.google.status.docsScopesMissing", { email: account.account_email })}
                    </div>
                  )}
                  {needsReauth && (
                    <div className="settings-row-hint" style={{ color: "var(--color-warning, #d97706)" }}>
                      {t("settings.google.status.reconnectNeeded", { email: account.account_email })}
                      {account.last_error && ` — ${account.last_error}`}
                    </div>
                  )}
                  {!account && (
                    <div className="settings-row-hint">{t("settings.google.status.notConnected")}</div>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "var(--space-1)" }}>
                {showReconnect && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void handleConnectGoogle(slot)}
                    disabled={googleConnecting}
                    aria-disabled={googleConnecting}
                    aria-label={googleConnecting
                      ? t("settings.connections.google.connecting.aria", { label })
                      : missingScopes
                      ? t("settings.connections.google.reconnectDocs.aria", { label })
                      : needsReauth
                      ? t("settings.connections.google.reconnect.aria", { label })
                      : t("settings.connections.google.connect.aria", { label })}
                    style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "var(--text-xs)" }}
                  >
                    <GoogleIcon size={14} />
                    {googleConnecting
                      ? t("settings.connections.google.connecting")
                      : missingScopes
                      ? t("settings.connections.google.reconnectDocs")
                      : needsReauth
                      ? t("settings.connections.google.reconnect")
                      : t("settings.connections.google.connect")}
                  </button>
                )}
                {account && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void handleDisconnectGoogle(slot)}
                    disabled={googleDisconnecting !== null}
                    aria-disabled={googleDisconnecting !== null}
                    aria-label={t("settings.connections.google.remove.aria", { label })}
                    style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", padding: "2px 8px" }}
                  >
                    {googleDisconnecting === slot ? t("settings.connections.google.removing") : t("settings.connections.google.remove")}
                  </button>
                )}
              </div>
            </div>
          )
        })}

        {/* D.1: role="alert" gives implicit aria-live="assertive" so errors announce immediately */}
        {googleError && (
          <div role="alert">
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-error, #c0392b)", marginTop: "var(--space-1)" }}>
              {googleError}
            </div>
          </div>
        )}
      </div>

      {/* CONNECTED SERVICES — non-Google integrations, read-only inventory */}
      <div className="settings-section">
        <div className="settings-section-label">{t("settings.connections.heading")}</div>

        {connectorsError && (
          <div role="alert">
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-error, #c0392b)" }}>
              {connectorsError}
            </div>
          </div>
        )}

        {connectors === null && !connectorsError && (
          <div className="settings-row-hint">{t("settings.connections.loading")}</div>
        )}

        <div aria-live="polite" aria-atomic="false">
          {connectors?.filter((row) => !row.id.startsWith("google-")).map((row) => {
            const mcpName = row.kind === "mcp" ? mcpServerNameFromId(row.id) : null
            return (
              <div key={row.id} className="settings-row">
                <div>
                  <div className="settings-row-label">
                    {t(row.label as TranslationKey)}
                    {mcpName ? `: ${mcpName}` : ""}
                  </div>
                  <div className="settings-row-hint" style={{ color: STATUS_COLOR[row.status] }}>
                    {t(STATUS_LABEL_KEY[row.status])}
                    {row.detail ? ` — ${row.detail}` : ""}
                  </div>
                  {googleBroken > 0 && isGoogleServerId(row.id) && (
                    <div className="settings-row-hint">
                      {t("settings.connections.googleServerSummary", { connected: googleConnected, broken: googleBroken })}
                    </div>
                  )}
                  {(row.status === "not_wired" || row.status === "unavailable") && row.reason && (
                    <div className="settings-row-hint">{t(row.reason as TranslationKey)}</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}
